import React, { Component } from 'react';
import tw, { styled } from 'twin.macro';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import { logErrorToCloudReporting } from '../../../shared/utils';
import BugReportForm from '../../partials/bug-report-form';
import { ButtonText } from '../../tw-partials/button';

const Button = styled(ButtonText)`
  ${tw`px-2 py-1 rounded-lg ml-4 hover:text-blue-800`};
  line-height: 1;

  &.undo {
    color: #fc830a;

    &:hover {
      color: #fc830adb;
    }
  }
`;

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);

    this.state = {
      hasLocalError: false,
      reportOpen: false,
      formId: ''
    };
  };

  componentDidUpdate(prevProps, prevState) {
    const { hasError } = this.props;

    if (prevProps.hasError !== hasError && !hasError) {
      this.resetError();
    }
  };

  static getDerivedStateFromError(error) {
    return {
      hasLocalError: true
    };
  };

  componentDidCatch(error, info) {
    const { setErrorState, title, match } = this.props;
    const { formId = 'N/A' } = match.params;
    const errorInfo = JSON.stringify(info);
    
    const data = `(${title} via Error Boundary) -> ${error}.\nForm Id: ${formId}\nInfo: ${errorInfo}`;
    logErrorToCloudReporting(data);
    
    // eslint-disable-next-line no-console
    this.setState({ error, formId });

    toast.error('An error occurred', {
      toastId: 'error-boundary',
    });

    setErrorState(true);
  };

  submitReport = (message) => {
    this.closeModal(message);
  };

  handleReportForm = () => {
    this.setState((prevState) => ({
      reportOpen: !prevState.reportOpen,
    }));
  };

  resetError = () => {
    this.setState({
      hasLocalError: false,
    });
  };

  render() {
    const { undoProgress, children, canUndo, content } = this.props;
    const { hasLocalError, reportOpen } = this.state;
    
    if (!hasLocalError) return children;
    
    return (
      <>
        <p>
          {content || 'An error has occurred. Please submit a crash report and reload to clear errors'}
          
          {canUndo && (
            <Button onClick={undoProgress} className="undo" type="primary">
              Undo last action
            </Button>
          )}

          <Button type="primary" onClick={this.handleReportForm}>
            Submit report
          </Button>
        </p>

        {reportOpen && <BugReportForm handleReportForm={this.handleReportForm} />}
      </>
    );
  }
}

ErrorBoundary.defaultProps = {
  hasError: false,
  canUndo: false,
  content: ''
};

ErrorBoundary.propTypes = {
  match: PropTypes.objectOf(PropTypes.any).isRequired,
  setErrorState: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  content: PropTypes.string,
  hasError: PropTypes.bool,
  canUndo: PropTypes.bool
};
